import { Banner } from '@fountain/ripple/banner';
import { Button } from '@fountain/ripple/button';
import { Select } from '@fountain/ripple/select';
import { Text } from '@fountain/ripple/text';
import { WorkflowEditorService } from 'api-clients/monolith';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { StageSettingCard } from 'containers/WorkflowEditor/components';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';
import { useApiService } from 'hooks/useApiService';
import globalMessages from 'shared/global/messages';

import { messages as legacy } from './messages';
import { ReportFilter, ReportFilters, ReportMappings } from './ReportMappings';
import { ReportRequests } from './ReportRequests';
import { messages } from './ReportStageSettings.messages';
import type { RehireCheckStageProps } from './RehireCheckStageSettings';
import { ReportTestScreen } from './ReportTestScreen';
import { ReportTestInput, useReportTest } from './useReportTest';

type Config = ReportTestInput['config'];
type Binding = keyof ReportTestInput['bindings'];
type Parameter = Config['reports'][number]['parameters'][number];
const fields: Binding[] = [
  'first_name',
  'preferred_first_name',
  'last_name',
  'personal_email',
  'phone',
  'known_employee_id',
];

export function ReportStageSettings({
  stage,
  enabled,
  accountId,
  children,
}: RehireCheckStageProps & {
  enabled: boolean;
  accountId: string;
  children: React.ReactNode;
}) {
  const {
    setStage,
    setCanSave,
    openingExternalId,
    updateStageResult,
    submittedStageExtra,
    resetUpdateStageResult,
  } = useContext(StageContext);
  const { funnelSlug } = useParams<{ funnelSlug?: string }>();
  const intl = useIntl();
  const t = intl.formatMessage;
  const container = useRef<HTMLDivElement>(null);
  const testPanel = useRef<HTMLDivElement>(null);
  const testButton = useRef<HTMLButtonElement>(null);
  const report =
    Boolean(stage.extra?.report_config) ||
    ![null, undefined, 'synced'].includes(stage.extra?.source_mode);
  const config = stage.extra?.report_config;
  const [configRevision, setConfigRevision] = useState(0);
  const [inputRevision, setInputRevision] = useState(0);
  const [previousConfig, setPreviousConfig] = useState(config);
  if (config !== previousConfig) {
    setPreviousConfig(config);
    setConfigRevision(revision => revision + 1);
  }
  const [bindings, setBindings] = useState<ReportTestInput['bindings']>({});
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (testing)
      (testPanel.current?.querySelector('input') ?? testPanel.current)?.focus();
  }, [testing]);
  const [purpose, setPurpose] =
    useState<ReportTestInput['purpose']>('discover');
  const [submitted, setSubmitted] = useState(false);
  // Ownership survives edits and collisions without adding UI keys to API payloads.
  const parameterOwners = useRef(new WeakMap<Parameter, string>());
  const [filters, setFilters] = useState<ReportFilter[]>(() => {
    const distinct = new Map<string, ReportFilter>();
    for (const item of config?.reports ?? [])
      for (const parameter of item.parameters) {
        if ('applicant_field' in parameter.value) {
          const field = parameter.value.applicant_field;
          const identity = JSON.stringify([parameter.key, field]);
          let filter = distinct.get(identity);
          if (!filter) {
            filter = {
              key: uuid(),
              field,
              name: parameter.key,
              editable: true,
            };
            distinct.set(identity, filter);
          }
          parameterOwners.current.set(parameter, filter.key);
        }
      }
    return [...distinct.values()];
  });
  const catalog = useApiService(
    useCallback(
      () =>
        WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckConnectors(
          'report',
          stage.external_id,
          funnelSlug,
        ),
      [stage.external_id, funnelSlug],
    ),
    { enabled: enabled && Boolean(funnelSlug) },
  );
  const connections =
    catalog.result.status === 'ready' ? catalog.result.data.connectors : [];
  const available =
    enabled &&
    stage.extra?.source_mode === 'report' &&
    config?.version === 1 &&
    Boolean(openingExternalId) &&
    connections.some(connection => connection.uuid === config.connector_uuid);
  const tester = useReportTest({
    enabled: report && available,
    accountId,
    stageExternalId: stage.external_id,
    funnelExternalId: openingExternalId ?? '',
    configRevision,
    inputRevision,
  });
  useEffect(() => {
    if (report) setCanSave(available);
  }, [report, available, setCanSave]);
  const saveErrors =
    updateStageResult.status === 'error' &&
    submittedStageExtra &&
    'report_config' in submittedStageExtra &&
    submittedStageExtra.report_config === config
      ? updateStageResult.error?.errors
      : undefined;
  useEffect(() => {
    if (saveErrors)
      container.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.focus();
  }, [saveErrors]);
  const update = (next: Config) => {
    tester.onInputChange();
    resetUpdateStageResult();
    setStage(previous => ({
      ...previous,
      extra: {
        ...previous.extra,
        report_config: {
          ...next,
          reports: next.reports.map(item => ({
            ...item,
            columns: {
              ...item.columns,
              person_ids: item.columns.person_ids ?? [],
              emails: item.columns.emails ?? [],
              phones: item.columns.phones ?? [],
              active_values: item.columns.active_values ?? [],
              eligible_values: item.columns.eligible_values ?? [],
            },
          })),
        },
      },
    }));
  };
  const reportErrors = useMemo(
    () =>
      Object.fromEntries(
        (config?.reports ?? []).map((item, index) => {
          const errors: { name?: string; path?: string } = {};
          for (const field of ['name', 'path'] as const) {
            if (
              (submitted && !item[field].trim()) ||
              saveErrors?.report_config?.some(error =>
                error.startsWith(`reports.${index}.${field}:`),
              )
            )
              errors[field] = t(messages.required);
          }
          return [item.key, errors];
        }),
      ),
    [config, submitted, saveErrors, t],
  );
  const parameterError = (filter: ReportFilter, field: 'key' | 'value') =>
    config?.reports.some((item, reportIndex) =>
      item.parameters.some(
        (parameter, parameterIndex) =>
          parameterOwners.current.get(parameter) === filter.key &&
          saveErrors?.report_config?.some(error =>
            error.startsWith(
              `reports.${reportIndex}.parameters.${parameterIndex}.${field}:`,
            ),
          ),
      ),
    );
  const filterErrors =
    submitted || saveErrors
      ? filters.flatMap(filter => [
          ...(!fields.includes(filter.field as Binding) ||
          parameterError(filter, 'value')
            ? [
                {
                  path: `filters.${filter.key}.field`,
                  code: 'missing_applicant_field' as const,
                },
              ]
            : []),
          ...(!filter.name.trim() || parameterError(filter, 'key')
            ? [
                {
                  path: `filters.${filter.key}.name`,
                  code: 'required_filter' as const,
                },
              ]
            : []),
        ])
      : [];
  const inputFields = fields.filter(field =>
    config?.reports.some(item =>
      item.parameters.some(
        parameter =>
          'applicant_field' in parameter.value &&
          parameter.value.applicant_field === field,
      ),
    ),
  );
  const fieldErrors = submitted
    ? Object.fromEntries(
        inputFields
          .filter(field => !bindings[field]?.trim())
          .map(field => [field, 'required' as const]),
      )
    : {};
  function openTest(nextPurpose: ReportTestInput['purpose']) {
    if (purpose !== nextPurpose) {
      tester.onInputChange();
      setInputRevision(revision => revision + 1);
    }
    setPurpose(nextPurpose);
    setTesting(true);
  }
  function run() {
    setSubmitted(true);
    if (
      !available ||
      !config ||
      !config.reports.length ||
      config.reports.some(item => !item.name.trim() || !item.path.trim()) ||
      filters.some(
        filter =>
          !filter.name.trim() || !fields.includes(filter.field as Binding),
      ) ||
      inputFields.some(field => !bindings[field]?.trim())
    ) {
      requestAnimationFrame(() =>
        container.current
          ?.querySelector<HTMLElement>('[aria-invalid="true"]')
          ?.focus(),
      );
      return;
    }
    void tester.run({
      config,
      bindings: Object.fromEntries(
        inputFields.map(field => [field, bindings[field] ?? '']),
      ),
      purpose,
    });
  }
  function filterParameter(
    filter: ReportFilter,
    previous?: Parameter,
  ): Parameter {
    const parameter = {
      ...previous,
      key: filter.name,
      value: { applicant_field: filter.field as Binding },
    };
    parameterOwners.current.set(parameter, filter.key);
    return parameter;
  }
  function changeFilters(next: ReportFilter[]) {
    setFilters(next);
    tester.onInputChange();
    if (!config) return;
    update({
      ...config,
      reports: config.reports.map(item => ({
        ...item,
        parameters: [
          ...item.parameters.flatMap(parameter => {
            if (!('applicant_field' in parameter.value)) return [parameter];
            const owner = parameterOwners.current.get(parameter);
            if (!owner) return [parameter];
            const after = next.find(filter => filter.key === owner);
            return after ? [filterParameter(after, parameter)] : [];
          }),
          ...next
            .filter(
              filter => !filters.some(before => before.key === filter.key),
            )
            .map(filter => filterParameter(filter)),
        ],
      })),
    });
  }
  const source = report ? `report:${config?.connector_uuid ?? ''}` : 'synced';
  const sourceItems = [
    { value: 'synced', label: t(messages.synced) },
    ...connections.map(connection => ({
      value: `report:${connection.uuid}`,
      label: connection.name,
    })),
  ];
  if (report && !sourceItems.some(item => item.value === source))
    sourceItems.push({
      value: source,
      label: config?.connector_uuid ?? t(messages.connection),
    });
  return (
    <div ref={container}>
      <Select
        label={t(messages.source)}
        value={source}
        items={sourceItems}
        onValueChange={value => {
          if (!enabled || !value || value === source) return;
          tester.reset();
          setBindings({});
          setTesting(false);
          setFilters([]);
          setStage(previous => ({
            ...previous,
            extra: {
              ...previous.extra,
              source_mode: value === 'synced' ? 'synced' : 'report',
              report_config:
                value === 'synced'
                  ? null
                  : { version: 1, connector_uuid: value.slice(7), reports: [] },
            },
          }));
        }}
        disabled={!enabled || catalog.result.status !== 'ready'}
      />
      {catalog.result.status === 'error' && (
        <Banner variant="error">
          {t(messages.unavailable)}
          <Button
            variant="text-brand"
            size="sm"
            onClick={() => void catalog.refetch()}
          >
            {t(legacy.retryMetadataButton)}
          </Button>
        </Banner>
      )}
      {!report ? (
        children
      ) : (
        <StageSettingCard
          title={t(legacy.settingsCardTitle)}
          variant="default"
          isLoading={enabled && catalog.result.isLoading}
        >
          {!available ? (
            <Banner variant="warning">{t(messages.unavailable)}</Banner>
          ) : (
            config && (
              <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
                {saveErrors && (
                  <Banner variant="error">
                    {t(legacy.dryRunErrorMessage)}
                  </Banner>
                )}
                {!testing && (
                  <>
                    <Text variant="subtitle1">{t(messages.subtitle)}</Text>
                    {!config.reports.length && (
                      <Text role="status">{t(messages.empty)}</Text>
                    )}
                    <ReportRequests
                      reports={config.reports}
                      onChange={reports =>
                        update({
                          ...config,
                          reports: reports.map(item =>
                            config.reports.some(
                              previous => previous.key === item.key,
                            )
                              ? item
                              : {
                                  ...item,
                                  parameters: [
                                    ...item.parameters.filter(
                                      parameter => 'literal' in parameter.value,
                                    ),
                                    ...filters.map(filter =>
                                      filterParameter(filter),
                                    ),
                                  ],
                                },
                          ),
                        })
                      }
                      onRun={() => openTest('discover')}
                      sources={tester.sources}
                      running={tester.status === 'running'}
                      fieldErrors={reportErrors}
                    />
                    <ReportFilters
                      filters={filters}
                      applicantFields={fields.map(field => ({
                        value: field,
                        label: t(messages[field]),
                      }))}
                      fieldErrors={filterErrors}
                      onFiltersChange={changeFilters}
                      onAdd={() =>
                        changeFilters([
                          ...filters,
                          { key: uuid(), field: '', name: '', editable: true },
                        ])
                      }
                    />
                    <Button
                      size="sm"
                      variant="secondary-brand"
                      ref={testButton}
                      onClick={() => openTest('evaluate')}
                      style={{ justifySelf: 'start' }}
                    >
                      {t(legacy.dryRunCardTitle)}
                    </Button>
                  </>
                )}
                {testing && (
                  <>
                    <Button
                      size="sm"
                      variant="text-neutral"
                      onClick={() => {
                        setTesting(false);
                        requestAnimationFrame(() =>
                          testButton.current?.focus(),
                        );
                      }}
                      style={{ justifySelf: 'start' }}
                    >
                      {t(globalMessages.back)}
                    </Button>
                    <div ref={testPanel} tabIndex={-1}>
                      <ReportTestScreen
                        mode="report-v1"
                        inputs={inputFields.map(field => ({
                          key: field,
                          label: t(messages[field]),
                          value: bindings[field] ?? '',
                          type:
                            field === 'personal_email'
                              ? 'email'
                              : field === 'phone'
                                ? 'tel'
                                : 'text',
                        }))}
                        sourceNames={Object.fromEntries(
                          config.reports.map(item => [item.key, item.name]),
                        )}
                        status={tester.status}
                        sources={tester.sources}
                        result={tester.result}
                        fieldErrors={fieldErrors}
                        onInputChange={(key, value) => {
                          tester.onInputChange();
                          setInputRevision(revision => revision + 1);
                          setBindings(previous => ({
                            ...previous,
                            [key]: value,
                          }));
                        }}
                        onRun={run}
                        onCancel={() => tester.reset()}
                      />
                    </div>
                    <ReportMappings
                      reports={config.reports}
                      discoveredColumns={[]}
                      fieldErrors={[]}
                      unavailableReason={t(messages.mappingUnavailable)}
                      onChange={() => {}}
                    />
                  </>
                )}
              </div>
            )
          )}
        </StageSettingCard>
      )}
    </div>
  );
}
